/**
 * pages/teams.ts —— 队伍管理列表（W2-4）
 * 来历：一期 [SEC:page-teams] 的 teamsHtml / [SEC:page-teams-write] 的四个写操作，
 *      渲染层重写、交互与版式不变。
 *
 * ## 与一期的四处差别（都不要"改回去"）
 * 1. **筛选、排序、成员 chip、页头三个数字全部由服务端算好下发**（`GET /api/teams/admin`）。
 *    一期是前端 `teamsVisible()` + `sum()` 现算的；二期前端一个都不许算（红线 4、前端纪律 2）。
 * 2. **成员管理弹窗是【攒够再存】**：一期点一下「添加」就改内存数据，二期在弹窗里攒出
 *    一份完整名单，点「保存」时一次 `PUT /:id/members` 全量替换。
 *    理由：中间态（先加后删）不该发到服务端，且每点一下发一次请求会让「至多 1 支在用队」
 *    这类拒绝在半路上蹦出来，用户已经点了五下不知道哪一下没生效。
 * 3. **变更队长只有管理员能做**（§五 5.4 补充 D4）。队长在成员弹窗里看到的队长行是只读的。
 * 4. **归档 / 恢复走独立接口**，恢复时服务端会重校一次重名（归档期间队名可能被新队占走）。
 *
 * ⚠ 「队伍编号」输入框是**只读**的：编号由服务端按「含已归档的最大编号 + 1」生成，
 *   创建后不可改，撞号只能归档重建（06 §四）。弹窗里显示的那个只是预览。
 */
import type { Team, TeamAdminItem, TeamAdminView, TeamCandidateView, TeamSortKey } from '@shared-types';
import { teams as teamApi } from '../api/index.js';
import { errText } from '../api/client.js';
import { closeModal, confirmModal, emptyHtml, flashAfterRender, openModal, toast } from '../shared/common.js';
import { esc, when } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { TYPE_LABEL } from '../shared/nav.js';
import { state } from '../shared/state.js';
import * as meta from '../shared/meta.js';
import { refreshCurrent } from '../router/render.js';
import { definePage } from './types.js';

/** 正式成员平铺上限：超过才折叠为 +N。6 人队占多数，平铺到 6 才不会一排就 +1 */
const MEM_FLAT = 6;

/* ===================== 渲染（纯函数） ===================== */

/** 能不能维护这支队的成员：本队队长或管理员。⚠ 这只是【显示】，拦截在服务端（红线 4） */
const canManage = (t: Team): boolean => meta.isAdmin() || meta.me()?.leadTeamId === t.id;

/** 支持人员分类明细，⚠ 只列非 0 的（一期 supList 同款） */
function supList(it: TeamAdminItem): string {
  const r = it.roster;
  return (
    [
      [TYPE_LABEL.volunteer, r.volunteers.length],
      [TYPE_LABEL.mentor, r.mentors.length],
      [TYPE_LABEL.external, r.externals.length],
    ] as Array<[string, number]>
  )
    .filter(([, n]) => n > 0)
    .map(([lab, n]) => `${lab} ${n}`)
    .join(' · ');
}

function rowHtml(it: TeamAdminItem): string {
  const t = it.team;
  const ms = it.roster.members;
  /* 队长并入正式成员列，排首位并加「· 队长」——三重标识（深蓝底 + 加粗 + 文字），
     不只靠颜色区分（05_技术与视觉规范.md §4 无障碍要求）。 */
  const chips =
    ms
      .slice(0, MEM_FLAT)
      .map((p) => {
        const lead = p.id === t.leadId;
        const off = p.active ? '' : ' off';
        return `<span class="mini${lead ? ' lead' : ''}${off}">${esc(p.name)}${
          lead ? ' · 队长' : ''
        }${p.active ? '' : ' · 已停用'}</span>`;
      })
      .join('') + when(ms.length > MEM_FLAT, `<span class="mini">+${ms.length - MEM_FLAT}</span>`);

  /* ⚠ 未指定队长是【防御性显示】：队长必填，正常流程走不到这里。
     但人员那侧把队长改成了别的类型、或队长被移出本队时会出现，必须看得见。 */
  const noLead = !it.leadName;
  const manage = canManage(t);
  const admin = meta.isAdmin();
  const ops = t.archived
    ? `<button class="tbtn" data-op="view|${esc(t.id)}">详情</button>` +
      when(admin, `<button class="tbtn" data-op="restore|${esc(t.id)}">恢复</button>`)
    : `<button class="tbtn" data-op="view|${esc(t.id)}">详情</button>` +
      when(admin, `<button class="tbtn" data-op="edit|${esc(t.id)}">编辑</button>`) +
      when(manage, `<button class="tbtn" data-op="mem|${esc(t.id)}">管理成员</button>`) +
      when(admin, `<button class="tbtn danger" data-op="archive|${esc(t.id)}">归档</button>`);

  return `
  <tr data-team="${esc(t.id)}" tabindex="0" class="${t.archived ? 'arch' : ''}" aria-label="查看 ${esc(t.name)} 详情">
    <td class="c-tm"><b><span class="no">${esc(t.code)}</span>${esc(t.name)}${when(
      t.archived,
      '<span class="badge arch">已归档</span>',
    )}</b><s title="${esc(t.topic)}">${esc(t.topic)}</s></td>
    <td><span class="num">${ms.length}</span> 人${
      noLead ? ' <span class="warn-txt"><span class="dot-alert"></span>未指定队长</span>' : ''
    }
      ${when(ms.length > 0, `<span class="sub12">${chips}</span>`)}</td>
    <td>${
      it.supportCount
        ? `<span class="num">${it.supportCount}</span> 人<span class="sub12">${esc(supList(it))}</span>`
        : '<span class="dash">—</span>'
    }</td>
    <td>${
      it.actAll
        ? `<span class="num">${it.actAll}</span> 次`
        : '<span class="dash">—</span>'
    }</td>
    <td><span class="ops">${ops}</span></td>
  </tr>`;
}

/** 可排序表头。⚠ 排序在服务端做，这里只显示当前状态并发起下一次请求 */
function th(key: TeamSortKey, label: string): string {
  const on = state.tm.sort === key;
  const dir = on ? (state.tm.dir === 'asc' ? '升序' : '降序') : '';
  return `<th class="${on ? 'on ' + state.tm.dir : ''}" ${
    on ? `aria-sort="${state.tm.dir === 'asc' ? 'ascending' : 'descending'}"` : ''
  }>
    <button class="sortbtn" data-tmsort="${key}" title="${esc(label + (on ? `：当前${dir}，点击切换` : '：点击排序'))}">${esc(label)}${IC.up}</button></th>`;
}

export function tableHtml(v: TeamAdminView): string {
  if (!v.items.length) {
    const newBtn = meta.isAdmin()
      ? `<button class="tbtn" data-op="new">＋ 新建队伍</button>`
      : '';
    /* 三种空态说三句不同的话——说同一句会让人以为筛选坏了（一期同款）。 */
    if (state.tm.q.trim()) {
      return emptyHtml(
        EIC.search,
        '没有匹配的队伍',
        '试试其他队名、课题或队长姓名',
        '<button class="tbtn" data-op="clearq">清空搜索</button>',
      );
    }
    if (!state.tm.arch && v.archivedCount > 0) {
      return emptyHtml(
        EIC.team,
        '当前没有生效的队伍',
        '勾选「显示已归档」可查看已归档队伍的档案',
        newBtn,
      );
    }
    return emptyHtml(EIC.team, '还没有任何队伍', '新建第一支队伍开始运营', newBtn);
  }
  /* 🔴 2026-09-22：「状态」列删掉，改为**只给归档队**在队名右侧挂一个标签；
     腾出来的位置放「累计打卡」。⚠ 生效是常态，一整列全是同一个「生效」标签等于没信息量。 */
  return `
  <table class="matrix tt">
    <colgroup><col class="tg-team"><col class="tg-mem"><col class="tg-sup">
      <col class="tg-act"><col class="tg-op"></colgroup>
    <thead><tr>
      ${th('id', '队伍')}${th('mem', '正式成员')}${th('sup', '支持人员')}
      ${th('act', '累计打卡')}<th>操作</th>
    </tr></thead>
    <tbody>${v.items.map(rowHtml).join('')}</tbody>
  </table>`;
}

/**
 * 页头统计行。⚠ 只写「有内容」的那几段：
 *   一支都没归档时不写「0 支已归档」（一个恒为 0 的数字只会让人以为归档坏了），
 *   没有支持人员时不写支持人员。数字全部由服务端算好（红线 4）。
 */
function headSub(v: TeamAdminView): string {
  const seg = v.archivedCount
    ? [`<span class="num">${v.activeCount}</span> 支生效`, `<span class="num">${v.archivedCount}</span> 支已归档`]
    : [`<span class="num">${v.activeCount}</span> 支队伍`];
  seg.push(`正式成员 <span class="num">${v.memberCount}</span> 人`);
  if (v.supportCount) seg.push(`支持人员 <span class="num">${v.supportCount}</span> 人`);
  return seg.join(' · ');
}

export function teamsHtml(v: TeamAdminView): string {
  return `
  <div class="page">
    <div class="pagehead">
      <div>
        <h1>队伍管理</h1>
        <p class="ph-sub">${headSub(v)}</p>
      </div>
    </div>

    <div class="toolbar">
      <div class="search">
        <span class="si" aria-hidden="true">${IC.search}</span>
        <input type="text" id="tm-q" value="${esc(state.tm.q)}" placeholder="搜索队名、课题或队长" aria-label="搜索队伍">
        <button class="sx" data-op="clearq" aria-label="清空搜索" style="${state.tm.q ? '' : 'display:none'}">${IC.x}</button>
      </div>
      <label class="chk"><input type="checkbox" id="tm-arch" ${state.tm.arch ? 'checked' : ''}>显示已归档</label>
      <span class="cnt" id="tm-cnt">显示 <span class="num">${v.items.length}</span> / <span class="num">${v.total}</span> 支</span>
      ${when(meta.isAdmin(), `<button class="btn primary" data-op="new">${IC.plus}新建队伍</button>`)}
    </div>

    <div class="panel" id="tm-panel">${tableHtml(v)}</div>
  </div>`;
}

/* ===================== 写操作：新建 / 编辑 ===================== */

interface FormValue {
  name: string;
  topic: string;
  joinFrom: number;
  joinTo: number;
  leadId: string;
}

const opt = (v: string | number, cur: string | number, lab: string): string =>
  `<option value="${esc(v)}"${String(v) === String(cur) ? ' selected' : ''}>${esc(lab)}</option>`;

function markField(root: ParentNode, key: string, msg: string): void {
  root.querySelectorAll('.fld').forEach((f) => f.classList.remove('bad'));
  const f = root.querySelector(`[data-f="${key}"]`);
  if (!f) return toast(msg);
  f.classList.add('bad');
  const err = f.querySelector('.err');
  if (err) err.textContent = msg;
  f.scrollIntoView({ block: 'center' });
  f.querySelector<HTMLElement>('input:not([readonly]),select')?.focus();
}

/**
 * 新建 / 编辑队伍（`it` 为 null 表示新建）。
 * ⚠ **队长的候选池两种情况不一样**（需求全文「弹窗字段」）：
 *   新建 → 未加入任何在用队伍的 active 正式成员，选定后自动成为本队第一名正式成员；
 *   编辑 → 本队现有 active 正式成员（换队长而已，不顺手拉人进队）。
 */
export async function openTeamModal(v: TeamAdminView | null, t: Team | null): Promise<void> {
  const isNew = t === null;

  /* 编辑时的候选要现拉一次：队伍页的整页出参里只带了「新建用」的那个池子，
     两个池子都塞进列表出参会让每行都背着一份人员清单。 */
  let pool = v?.leadPool ?? [];
  if (!isNew && t) {
    try {
      const cv = await teamApi.candidates(t.id);
      pool = cv.leadPool;
    } catch (e) {
      // 队长候选拉不到不该挡住改队名：给空池子 + 明确提示，其余字段照常能改
      pool = [];
      toast(errText(e));
    }
  }
  const noPool = pool.length === 0;

  /* 参与周期的期号选项来自 meta 缓存的周期表。
     ⚠ 一期是 PERIODS 常量；二期周期是管理员临近再建的，**可能一期都还没有**（红线 3.5）——
       那时给一个「第 1 期」保底，否则两个下拉是空的，新建直接走不下去。 */
  const nos = new Set(meta.allPeriods().map((p) => p.no));
  /* ⚠ 【本队现有的起止期号必须也在选项里】，哪怕那两期还没建：
     周期是管理员「临近再建」的，一支参与到第 12 期的队伍在第 3 期时，
     选项里没有 12 → 下拉会静默回落到第一项，点一下「保存修改」就把 joinTo 从 12 改成了 1，
     而界面上什么都不会提示。 */
  if (t) {
    nos.add(t.joinFrom);
    nos.add(t.joinTo);
  }
  const periodNos = nos.size ? [...nos].sort((a, b) => a - b) : [1];
  const curNo = meta.currentPeriod()?.no ?? periodNos[0] ?? 1;

  const init: FormValue = {
    name: t?.name ?? '',
    topic: t?.topic ?? '',
    joinFrom: t?.joinFrom ?? curNo,
    // 新队默认「本期开始、再参加一期」，但不越过已有的最大期号
    joinTo: t?.joinTo ?? Math.min(curNo + 1, periodNos[periodNos.length - 1] ?? curNo),
    leadId: t?.leadId ?? (isNew ? (pool[0]?.id ?? '') : ''),
  };

  const read = (root: ParentNode): FormValue => ({
    name: (root.querySelector<HTMLInputElement>('#tf-name')?.value ?? '').trim(),
    topic: (root.querySelector<HTMLInputElement>('#tf-topic')?.value ?? '').trim(),
    joinFrom: Number(root.querySelector<HTMLSelectElement>('#tf-from')?.value ?? curNo),
    joinTo: Number(root.querySelector<HTMLSelectElement>('#tf-to')?.value ?? curNo),
    leadId: noPool ? '' : (root.querySelector<HTMLSelectElement>('#tf-lead')?.value ?? ''),
  });

  const periodOpts = (cur: number) => periodNos.map((n) => opt(n, cur, `第 ${n} 期`)).join('');

  const body = `
    <div class="fld"><label>队伍编号</label>
      <input type="text" readonly value="${esc(isNew ? (v?.nextCode ?? '') : (t?.code ?? ''))}" aria-label="队伍编号">
      <div class="hint">${
        isNew
          ? '取现有全部队伍（<b>含已归档</b>）中最大编号 + 1，由服务端生成；提交时会重新取一次。'
          : '队伍编号<b>创建后不可修改</b>，撞号只能归档重建。'
      }</div></div>
    <div class="fld" data-f="name"><label>队伍名称<span class="req">*</span></label>
      <input type="text" id="tf-name" maxlength="20" value="${esc(init.name)}" placeholder="如：智汇未来队">
      <div class="hint">不得与<b>生效</b>队伍重名；已归档队伍不占用名称。</div>
      <div class="err"></div></div>
    <div class="fld" data-f="topic"><label>课题<span class="req">*</span></label>
      <input type="text" id="tf-topic" maxlength="45" value="${esc(init.topic)}" placeholder="如：投研数据治理底座">
      <div class="err"></div></div>
    <div class="fld" data-f="cyc"><label>参与周期<span class="req">*</span></label>
      <div class="fld-2">
        <select id="tf-from" aria-label="起始期">${periodOpts(init.joinFrom)}</select>
        <select id="tf-to" aria-label="结束期">${periodOpts(init.joinTo)}</select>
      </div>
      <div class="hint">起始期 / 结束期，结束期不得早于起始期。<b>仅作档案展示，不参与任何统计。</b></div>
      <div class="err"></div></div>
    <div class="fld" data-f="lead"><label>队长<span class="req">*</span></label>
      ${
        noPool
          ? `<div class="fld-ro">${
              isNew
                ? '暂无可选人员，请先在管理后台的「人员管理」中新增正式成员'
                : '本队暂无可担任队长的正式成员，请先用「管理成员」加人'
            }</div>`
          : `<select id="tf-lead">${pool
              .map((p) => opt(p.id, init.leadId, `${p.name}（${p.dept}）`))
              .join('')}</select>`
      }
      <div class="hint">${
        isNew
          ? '从<b>未加入任何生效队伍</b>的正式成员中选择；选定后自动成为本队第一名正式成员。'
          : '从<b>本队现有正式成员</b>中选择。变更队长仅管理员可操作。'
      }</div>
      <div class="err"></div></div>`;

  const rec = openModal({
    title: isNew ? '新建队伍' : `编辑队伍 · ${t?.code ?? ''} ${t?.name ?? ''}`,
    body,
    footer: `<button class="btn" data-mclose>取消</button>
            <button class="btn primary" data-save${noPool ? ' disabled' : ''}>${isNew ? '创建队伍' : '保存修改'}</button>`,
    dirty() {
      if (noPool) return false;
      const val = read(rec.mask);
      return (Object.keys(init) as Array<keyof FormValue>).some((k) => val[k] !== init[k]);
    },
    onMount(root) {
      root.querySelector('[data-save]')?.addEventListener('click', () => void submit(root));
      root.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' && (ev.target as Element)?.tagName === 'INPUT') {
          ev.preventDefault();
          void submit(root);
        }
      });
    },
  });

  async function submit(root: ParentNode): Promise<void> {
    const val = read(root);
    /* ⚠ 前端只拦「明显没填」，**不是校验**：重名、期号倒挂、队长资格都由服务端判，
       失败文案直接用服务端返回的（红线 4 / 前端纪律 2）。 */
    if (!val.name) return markField(root, 'name', '请填写队伍名称');
    if (!val.topic) return markField(root, 'topic', '请填写课题');
    if (!val.leadId) return markField(root, 'lead', '请选择队长');

    const btn = (root as ParentNode).querySelector<HTMLButtonElement>('[data-save]');
    if (btn) btn.disabled = true;
    try {
      const saved = isNew
        ? await teamApi.createTeam({
            name: val.name,
            topic: val.topic,
            leadId: val.leadId,
            joinFrom: val.joinFrom,
            joinTo: val.joinTo,
          })
        : await teamApi.updateTeam(t?.id as string, {
            name: val.name,
            topic: val.topic,
            leadId: val.leadId,
            joinFrom: val.joinFrom,
            joinTo: val.joinTo,
          });
      closeModal(true);
      flashAfterRender(saved.id);
      await afterTeamWrite(
        isNew ? `已新建队伍 ${saved.code} ${saved.name}` : `已更新队伍 ${saved.code} ${saved.name}`,
      );
    } catch (e) {
      // ⚠ 错误留在弹窗里，不 toast：填的内容还在，人要在原地改
      markField(root, 'name', errText(e));
    } finally {
      if (btn) btn.disabled = false;
    }
  }
}

/**
 * 写操作后的统一收尾：刷 meta（守卫要用）→ 就地重渲染 → 轻提示。
 * ⚠ `meta.refresh()` 不能省：动过队伍之后守卫还拿着旧列表，
 *   表现是「刚建的队点不进去」（shared/meta.ts 纪律 2）。
 */
async function afterTeamWrite(msg: string): Promise<void> {
  await meta.refresh();
  refreshCurrent();
  toast(msg);
}

/* ===================== 写操作：管理成员 ===================== */

/**
 * 成员管理弹窗（宽档，左右两栏）。
 * ⚠ 与一期最大的差别：**攒够再存**。左右两栏改的是本地名单，点「保存」才一次全量提交。
 * ⚠ 队长行的「移除」是禁用的——服务端也会拒（红线 4：前端禁用不算校验）。
 * ⚠ 2026-09-14 用户定：**不显示工号**，行内只留姓名 + 部门——训练营里同部门重名不存在，工号只会挤占标签位置；搜索也不再匹配工号。
 */
export async function openMemberModal(t: Team): Promise<void> {
  let cv: TeamCandidateView;
  try {
    cv = await teamApi.candidates(t.id);
  } catch (e) {
    return void toast(errText(e));
  }

  const admin = meta.isAdmin();
  const all = new Map<string, TeamCandidateView['candidates'][number]['person']>();
  for (const c of cv.candidates) all.set(c.person.id, c.person);
  for (const g of [cv.roster.members, cv.roster.volunteers, cv.roster.mentors, cv.roster.externals]) {
    for (const p of g) all.set(p.id, p);
  }
  const blockedBy = new Map(cv.candidates.map((c) => [c.person.id, c]));

  const initIds = [
    ...cv.roster.members,
    ...cv.roster.volunteers,
    ...cv.roster.mentors,
    ...cv.roster.externals,
  ].map((p) => p.id);
  let picked = [...initIds];
  let leadId = cv.leadId;
  let q = '';
  /* 🔴 2026-09-14：本队名单里【以志愿者身份加入】的正式成员。
     分组一律看它，**不看 `type`**——这些人的 type 仍是 member。 */
  const initVol = cv.roster.volunteers.filter((p) => p.asVolunteer).map((p) => p.id);
  const vol = new Set(initVol);
  /** 他正式在哪支队（标签用）：已在本队的取 roster 下发的，人员库里的取 busyTeam */
  const formalOf = new Map<string, { code: string; name: string } | null>();
  for (const p of cv.roster.volunteers) if (p.asVolunteer) formalOf.set(p.id, p.formalTeam ?? null);
  for (const c of cv.candidates) if (c.volunteerOnly) formalOf.set(c.person.id, c.busyTeam);
  const roleOf = (p: { id: string; type: string }): string => (vol.has(p.id) ? 'volunteer' : p.type);

  const inTeam = (): Array<{ p: NonNullable<ReturnType<typeof all.get>>; }> =>
    picked
      .map((id) => all.get(id))
      .filter((p): p is NonNullable<typeof p> => !!p)
      .map((p) => ({ p }));

  const group = (title: string, type: string): string => {
    const arr = inTeam()
      .map((x) => x.p)
      .filter((p) => roleOf(p) === type);
    // 队长排首位，其余按加入顺序（服务端下发时已按拼音排过）
    arr.sort((a, b) => (a.id === leadId ? -1 : b.id === leadId ? 1 : 0));
    const rows = arr.length
      ? arr
          .map((p) => {
            const isLead = p.id === leadId;
            return `<div class="prow">
        <span class="pn">${esc(p.name)}${p.active ? '' : '<span class="tag mute">已停用</span>'}</span>${
          isLead ? '<span class="tag">队长</span>' : ''
        }${when(vol.has(p.id), volTag(p.id))}
        <span class="pm">${esc(p.dept)}</span>
        <span class="pa">${
          isLead
            ? '<button class="tbtn danger" disabled title="请先变更队长">移除</button>'
            : `<button class="tbtn danger" data-rm="${esc(p.id)}">移除</button>`
        }</span></div>`;
          })
          .join('')
      : '<div class="prow"><span class="pm">暂无</span></div>';
    return `<div class="mm-g">${esc(title)}（${arr.length}）</div>${rows}`;
  };

  /** 「T03队员」：以志愿者身份在本队的正式成员，标出他的正式队（2026-09-14 用户定文案） */
  const volTag = (id: string): string => {
    const f = formalOf.get(id);
    return `<span class="tag mute" title="正式成员，在本队以志愿者身份参与（只读）">${
      f ? `${esc(f.code)}队员` : '正式成员'
    }</span>`;
  };

  const left = (): string =>
    group(TYPE_LABEL.member, 'member') +
    group(TYPE_LABEL.volunteer, 'volunteer') +
    group(TYPE_LABEL.mentor, 'mentor') +
    group(TYPE_LABEL.external, 'external');

  const right = (): string => {
    const key = q.trim().toLowerCase();
    let arr = cv.candidates.filter((c) => !picked.includes(c.person.id));
    if (key) {
      arr = arr.filter((c) =>
        `${c.person.name}${c.person.dept}${TYPE_LABEL[c.person.type]}`
          .toLowerCase()
          .includes(key),
      );
    }
    if (!arr.length) {
      return emptyHtml(EIC.search, '没有匹配的人员', '换个姓名或部门 / 单位试试', '', 'sm');
    }
    /* ⚠ 顺序由服务端给：可添加的在前、不可选的沉底（2026-08-03 用户明确要求）。
       前端不再排一次——两处排序必然有一天不一致（前端纪律 2）。 */
    return arr
      .map((c) => {
        const p = c.person;
        const why = !p.active
          ? '<span class="tag mute">已停用</span>'
          : c.busyTeam
            ? `<span class="tag mute" title="${esc(c.busyTeam.code + ' ' + c.busyTeam.name)}">${esc(c.busyTeam.code)}队员</span>`
            : '';
        const tip = '该人员已停用，请先在管理后台启用';
        /* 🔴 2026-09-14：已在别队的正式成员不再是灰按钮，改为「以志愿者加入」——
           正式成员至多 1 支，志愿者身份不限；加入后在本队只读、不占正式名额。 */
        const addBtn = c.volunteerOnly
          ? `<button class="tbtn" data-add="${esc(p.id)}" title="正式成员至多 1 支队伍；以志愿者身份加入后在本队只读，打卡可被勾选">以志愿者加入</button>`
          : `<button class="tbtn" data-add="${esc(p.id)}">添加</button>`;
        return `<div class="prow ${c.addable ? '' : 'off'}">
        <span class="pn">${esc(p.name)}</span><span class="tag mute">${TYPE_LABEL[p.type]}</span>
        <span class="pm">${esc(p.dept)}</span>
        ${why}
        <span class="pa">${
          c.addable ? addBtn : `<button class="tbtn" disabled title="${esc(tip)}">添加</button>`
        }</span></div>`;
      })
      .join('');
  };

  const foot = (): string => {
    const list = inTeam().map((x) => x.p);
    const n = list.filter((p) => roleOf(p) === 'member').length;
    const sup = list.length - n;
    return (
      `<span>正式 <b class="num">${n}</b> 人 · 支持 <b class="num">${sup}</b> 人</span>` +
      // ⚠ 软提示，不阻止保存：4—5 人是训练营的建议不是硬约束（需求全文「状态穷举」）
      when(n < 4 || n > 5, `<span class="softwarn">${IC.warn}当前 ${n} 人，训练营建议每队 4—5 人</span>`)
    );
  };

  const leadRow = (): string => {
    const ms = inTeam()
      .map((x) => x.p)
      .filter((p) => roleOf(p) === 'member' && p.active);
    if (!admin) {
      const name = leadId ? (all.get(leadId)?.name ?? '未指定') : '未指定';
      return `<span class="pm">队长：${esc(name)}　<em>变更队长请联系项目组管理员</em></span>`;
    }
    return `<span class="pm">队长</span>
      <select id="mm-lead" aria-label="选择队长">
        ${ms.map((p) => opt(p.id, leadId ?? '', p.name)).join('') || opt('', '', '（本队暂无可选正式成员）')}
      </select>`;
  };

  const rec = openModal({
    title: `管理成员 · ${t.code} ${t.name}`,
    size: 'lg',
    body: `
      <div class="mm">
        <div class="mm-col">
          <div class="mm-h">本队人员 <em>移除后该人员回到正式人员库</em></div>
          <div class="mm-b" id="mm-left">${left()}</div>
        </div>
        <div class="mm-col">
          <div class="mm-h">人员库 <em>正式成员至多 1 支队，可以志愿者身份加入其他队</em></div>
          <div class="mm-b">
            <div class="search">
              <span class="si" aria-hidden="true">${IC.search}</span>
              <input type="text" id="mm-q" placeholder="搜索姓名或部门 / 单位" aria-label="搜索人员">
            </div>
            <div id="mm-right">${right()}</div>
          </div>
        </div>
      </div>
      <div class="mm-foot"><span id="mm-lead-row">${leadRow()}</span><span id="mm-foot">${foot()}</span></div>`,
    footer: `<button class="btn" data-mclose>取消</button>
            <button class="btn primary" data-save>保存</button>`,
    dirty: () =>
      picked.length !== initIds.length ||
      picked.some((id) => !initIds.includes(id)) ||
      vol.size !== initVol.length ||
      initVol.some((id) => !vol.has(id)) ||
      leadId !== cv.leadId,
    onMount(root) {
      const redraw = (side?: 'right'): void => {
        if (side !== 'right') {
          const l = root.querySelector('#mm-left');
          if (l) l.innerHTML = left();
          const lr = root.querySelector('#mm-lead-row');
          if (lr) lr.innerHTML = leadRow();
        }
        const r = root.querySelector('#mm-right');
        if (r) r.innerHTML = right();
        const f = root.querySelector('#mm-foot');
        if (f) f.innerHTML = foot();
      };

      root.addEventListener('click', (e) => {
        const el = e.target;
        if (!(el instanceof Element)) return;

        const add = el.closest<HTMLButtonElement>('[data-add]');
        if (add && !add.disabled) {
          const id = add.dataset.add as string;
          /* ⚠ 与按钮 disabled 同一个条件再判一次：防止绕过 UI 直接派发事件。
             真正的拦截仍在服务端（`canAddMember`）。 */
          const c = blockedBy.get(id);
          if (c?.addable && !picked.includes(id)) {
            picked.push(id);
            if (c.volunteerOnly) vol.add(id);
            redraw();
          }
          return;
        }
        const rm = el.closest<HTMLButtonElement>('[data-rm]');
        if (rm && !rm.disabled) {
          const id = rm.dataset.rm as string;
          if (id !== leadId) {
            picked = picked.filter((x) => x !== id);
            vol.delete(id);
            redraw();
          }
          return;
        }
        if (el.closest('[data-save]')) void save(root);
      });

      root.addEventListener('input', (e) => {
        const el = e.target;
        if (el instanceof HTMLInputElement && el.id === 'mm-q') {
          q = el.value;
          redraw('right');
        }
      });
      root.addEventListener('change', (e) => {
        const el = e.target;
        if (el instanceof HTMLSelectElement && el.id === 'mm-lead') {
          leadId = el.value || null;
          redraw();
        }
      });
    },
  });

  async function save(root: ParentNode): Promise<void> {
    const btn = root.querySelector<HTMLButtonElement>('[data-save]');
    if (btn) btn.disabled = true;
    try {
      /* ⚠ 队长只有管理员能改：非管理员一律回传原值，让服务端那条 403 不会被误触发。 */
      const volIds = picked.filter((id) => vol.has(id));
      const saved = await teamApi.saveMembers(t.id, picked, admin ? leadId : cv.leadId, volIds);
      closeModal(true);
      flashAfterRender(saved.id);
      const n = picked.filter((id) => {
        const p = all.get(id);
        return !!p && roleOf(p) === 'member';
      }).length;
      await afterTeamWrite(
        `已更新 ${saved.code} ${saved.name} 的人员构成，正式 ${n} 人 · 支持 ${picked.length - n} 人`,
      );
    } catch (e) {
      toast(errText(e));
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  void rec;
}

/* ===================== 写操作：归档 / 恢复 ===================== */

export function archiveTeam(t: Team): void {
  confirmModal({
    title: '归档队伍',
    okText: '确认归档',
    danger: true,
    html: `确认归档 <b>${esc(t.code)} ${esc(t.name)}</b>？<br>
      归档即解散该队：历史 OKR、双周报与打卡记录<b>全部保留</b>，随时可以恢复；
      该队退出本期考核，本期应提交清单与提交率分母相应减 1。<br>
      <b>队伍成员关联不会被清空</b>，恢复后人员构成原样回来。`,
    onOk: () => void run(true),
  });

  async function run(archived: boolean): Promise<void> {
    try {
      const saved = await teamApi.setArchived(t.id, archived);
      if (state.tm.arch) flashAfterRender(saved.id);
      await afterTeamWrite(`已归档 ${saved.code} ${saved.name}，历史数据保留`);
    } catch (e) {
      toast(errText(e));
    }
  }
}

export function restoreTeam(t: Team): void {
  confirmModal({
    title: '恢复队伍',
    okText: '确认恢复',
    html: `确认恢复 <b>${esc(t.code)} ${esc(t.name)}</b>？<br>
      恢复后该队重新纳入本期考核，计入队伍数与正式成员统计，本期提交率分母相应加 1。<br>
      ⚠ 归档期间队名可能已被新队占用，若重名会被服务端拒绝，需先改名再恢复。`,
    onOk: () => {
      void (async () => {
        try {
          const saved = await teamApi.setArchived(t.id, false);
          flashAfterRender(saved.id);
          await afterTeamWrite(`已恢复 ${saved.code} ${saved.name}，已重新纳入本期考核`);
        } catch (e) {
          toast(errText(e));
        }
      })();
    },
  });
}

/* ===================== 筛选与排序（只改 state，再让 render 重取） ===================== */

let searchTimer: ReturnType<typeof setTimeout> | null = null;
/** 下一次渲染后要不要把光标放回搜索框。⚠ 输入触发重渲染会丢焦点，靠它补回来 */
let refocusSearch = false;

function onSearchInput(value: string): void {
  state.tm.q = value;
  refocusSearch = true;
  // ⚠ 防抖：每敲一个字都发一次请求，13 支队不至于卡，但日志会被刷满
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => refreshCurrent(), 250);
}

/* ===================== 页面模块 ===================== */

export const teamsPage = definePage<TeamAdminView>({
  skeleton: 'table',

  load: ({ signal }) =>
    teamApi.adminView(
      {
        arch: state.tm.arch,
        q: state.tm.q,
        sort: state.tm.sort as TeamSortKey,
        dir: state.tm.dir,
      },
      { signal },
    ),

  html: (v) => teamsHtml(v),

  mounted() {
    // 从详情页返回时回到原来的滚动位置（需求全文「入口与出口」）
    const main = document.querySelector('#main');
    if (main && state.tm.scroll) main.scrollTop = state.tm.scroll;
    if (!refocusSearch) return;
    refocusSearch = false;
    const el = document.querySelector<HTMLInputElement>('#tm-q');
    if (!el) return;
    el.focus();
    // 光标放到末尾，否则继续输入会从头插入
    el.setSelectionRange(el.value.length, el.value.length);
  },

  onEvent(e, v) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (e.type === 'input' && t.id === 'tm-q') {
      return onSearchInput((t as HTMLInputElement).value);
    }
    if (e.type === 'change' && t.id === 'tm-arch') {
      state.tm.arch = (t as HTMLInputElement).checked;
      return refreshCurrent();
    }
    if (e.type !== 'click') return;

    /* 表头排序：同一列再点一次换方向。⚠ 排序本身在服务端做，这里只改 state 再重取 */
    const sortKey = t.closest<HTMLElement>('[data-tmsort]')?.dataset.tmsort;
    if (sortKey) {
      if (state.tm.sort === sortKey) state.tm.dir = state.tm.dir === 'asc' ? 'desc' : 'asc';
      else {
        state.tm.sort = sortKey;
        state.tm.dir = 'asc';
      }
      return refreshCurrent();
    }

    const op = t.closest<HTMLElement>('[data-op]')?.dataset.op;
    if (op) {
      const [kind, id] = op.split('|');
      const it = id ? v.items.find((x) => x.team.id === id) : null;
      if (kind === 'clearq') {
        state.tm.q = '';
        return refreshCurrent();
      }
      if (kind === 'new') return void openTeamModal(v, null);
      if (!it) return;
      if (kind === 'view') return void goDetail(it.team.id);
      if (kind === 'edit') return void openTeamModal(v, it.team);
      if (kind === 'mem') return void openMemberModal(it.team);
      if (kind === 'archive') return archiveTeam(it.team);
      if (kind === 'restore') return restoreTeam(it.team);
      return;
    }

    /* 点整行（非操作列）进详情 —— 一期同款。⚠ 操作列的按钮已在上面 return 掉了 */
    const row = t.closest<HTMLElement>('tr[data-team]');
    if (row?.dataset.team) return void goDetail(row.dataset.team);
  },
});

/** 进详情页：记住滚动位置与高亮行，返回时还原（需求全文「入口与出口」） */
function goDetail(id: string): void {
  state.tm.scroll = document.querySelector('#main')?.scrollTop ?? 0;
  state.hit = id;
  location.hash = '#/teams/' + id;
}
